'use client';

/**
 * Sales Page Modal Renderer
 *
 * Renders modals for the sales page based on modal ID.
 * You can use any modal component you want here.
 */

import { useTranslations } from 'next-intl';
import React from 'react';


import type { ModalConfig } from '../../page/components/InteractiveHtml/types';

import SalesContactForm from './SalesContactForm';

import { Popup } from '@components/Popup/Popup';

interface SalesPageModalRendererProps {
  config: ModalConfig;
  close: () => void;
}

export const SalesPageModalRenderer: React.FC<SalesPageModalRendererProps> = ({ config, close }) => {
  const renderContent = () => {
    switch (config.id) {
      case 'contact':
        return <ContactModalContent />;
      default:
        return (
          <div className="py-4">
            <p>Modal content for: {config.id || 'unknown'}</p>
            <p className="mt-2 text-sm text-gray-600">
              Create specific content for this modal in SalesPageModalRenderer.tsx
            </p>
          </div>
        );
    }
  };

  return (
    <Popup
      isOpen={true}
      close={close}
      title={config.title || ''}
      className={config.id === 'contact' ? 'max-w-[1400px]' : getModalSizeClass(config.size || 'xl')}
    >
      {renderContent()}
    </Popup>
  );
};

/**
 * Get Tailwind class for modal size
 */
const getModalSizeClass = (size?: 'sm' | 'md' | 'lg' | 'xl'): string => {
  switch (size) {
    case 'sm':
      return 'max-w-sm';
    case 'md':
      return 'max-w-md';
    case 'lg':
      return 'max-w-lg';
    case 'xl':
      return 'max-w-5xl';
    default:
      return 'max-w-2xl';
  }
};

/**
 * Contact Modal Content
 */
const ContactModalContent: React.FC = () => {
  const t = useTranslations('campaign.order');

  return (
    <div className="flex h-[80vh] max-h-[900px] flex-col overflow-y-auto lg:flex-row lg:overflow-hidden">
      {/* Left Panel - from CampaignOrderPage */}
      <div className="flex min-w-[320px] flex-col bg-[#f3f7fa] px-8 pb-6 pt-8 lg:w-[50%] lg:px-12">
        <span className="mb-2 text-[16px] font-medium leading-[20px] text-[#ff9800] lg:text-[18px] lg:leading-[22px]">
          {t('contact')}
        </span>
        <h1 className="mb-6 mt-2 text-[32px] font-bold leading-[40px] text-[#003554] lg:mb-8 lg:text-[48px] lg:leading-[56px]">
          {t('title')}
        </h1>
        <div className="mb-4 flex flex-col items-center lg:mb-6">
          <img
            src="/images/contact-support-illustration.png"
            alt={t('contactPerson.name')}
            className="mb-4 h-[100px] w-[100px] rounded-full object-cover lg:h-[140px] lg:w-[140px]"
          />
          <div className="flex w-full flex-col items-center">
            <span className="mb-1 text-center text-[20px] font-bold leading-[26px] text-[#003554] lg:text-[26px] lg:leading-[32px]">
              {t('contactPerson.name')}
            </span>
            <span className="mb-4 text-center text-[16px] font-normal leading-[20px] text-[#222] lg:mb-6 lg:text-[18px] lg:leading-[22px]">
              {t('contactPerson.position')}
            </span>
          </div>
        </div>
        <div className="mb-4 flex flex-col gap-3 lg:mb-6 lg:gap-4">
          <div className="flex items-center gap-3">
            <img src="/images/email.png" alt="Email" className="h-6 w-6 lg:h-7 lg:w-7" />
            <span className="text-[16px] font-bold leading-[20px] text-[#222] lg:text-[18px] lg:leading-[22px]">
              {t('contactPerson.email')}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <img src="/images/phone.png" alt="Phone" className="h-6 w-6 lg:h-7 lg:w-7" />
            <span className="text-[16px] font-bold leading-[20px] text-[#222] lg:text-[18px] lg:leading-[22px]">
              {t('contactPerson.phone')}
            </span>
          </div>
          <div className="flex hidden items-start gap-3">
            <img src="/images/chat.png" alt="Live chat" className="mt-1 h-6 w-6 lg:h-7 lg:w-7" />
            <div className="text-left">
              <span className="text-[16px] font-bold leading-[20px] text-[#222] lg:text-[18px] lg:leading-[22px]">
                {t('liveChat.title')}
              </span>
              <div className="mt-1 text-[14px] font-normal leading-[18px] text-[#222] lg:text-[16px] lg:leading-[20px]">
                {t('liveChat.description')}
              </div>
            </div>
          </div>
        </div>
        <a
          href="mailto:redakcja@trans.info"
          className="mx-auto mt-2 w-[90%] rounded-full bg-[#ff9800] py-2.5 text-[16px] font-bold text-white transition-colors hover:bg-[#e68900] lg:py-3 lg:text-[18px]"
        >
          {t('startChat')}
        </a>
      </div>

      {/* Right Panel - Form */}
      <div className="relative flex-1 bg-white px-8 pb-6 pt-8 lg:overflow-y-auto lg:px-12">
        <div className="">
          <h2 className="mb-6 mt-2 text-[22px] font-bold leading-[30px] text-[#00598c] lg:mb-10 lg:text-[28px] lg:leading-[36px]">
            {t('formTitle')}
            <br />
            {t('formSubtitle')}
          </h2>
          <SalesContactForm />
        </div>
      </div>
    </div>
  );
};
